import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { DestinationId, DestinationRunStatus, UploadAnalysis, VendorRouting } from '../lib/types';
import { deriveVendorRouting, validateUpload } from '../lib/csvUpload';
import { Stepper, type StepDef } from './ui/Stepper';
import { Step1Upload } from './steps/Step1Upload';
import { Step2Mapping } from './steps/Step2Mapping';
import { Step3Destinations } from './steps/Step3Destinations';
import { Step4Review } from './steps/Step4Review';
import { ProcessingView } from './views/ProcessingView';
import { ResultsView } from './views/ResultsView';
type Phase = 'wizard' | 'processing' | 'results';
const STEPS: StepDef[] = [{
  id: 1,
  label: 'Upload CSV',
  description: 'Select & validate'
}, {
  id: 2,
  label: 'Map fields',
  description: 'Match columns'
}, {
  id: 3,
  label: 'Destinations',
  description: 'Route leads'
}, {
  id: 4,
  label: 'Review',
  description: 'Confirm & submit'
}];
export function LeadUploader() {
  const [phase, setPhase] = useState<Phase>('wizard');
  const [step, setStep] = useState(1);
  const [analysis, setAnalysis] = useState<UploadAnalysis | null>(null);
  const [destinations, setDestinations] = useState<DestinationId[]>(['teli', 'sendgrid', 'five9']);
  const [routing, setRouting] = useState<VendorRouting[]>([]);
  const [vendor, setVendor] = useState<string | null>(null);
  const [uploadCount, setUploadCount] = useState(0);
  const [results, setResults] = useState<Record<DestinationId, DestinationRunStatus>>({
    teli: 'waiting',
    sendgrid: 'waiting',
    five9: 'waiting'
  });
  const validation = useMemo(() => analysis ? validateUpload(analysis.records, analysis.columns) : null, [analysis]);
  const totalRows = analysis?.file.rows ?? 0;
  function setUpload(nextAnalysis: UploadAnalysis) {
    setAnalysis(nextAnalysis);
    setRouting(nextAnalysis.routing);
    setStep(1);
  }
  function updateColumns(columns: UploadAnalysis['columns']) {
    setAnalysis(current => current ? {
      ...current,
      columns
    } : current);
    setRouting(current => analysis ? mergeRouting(deriveVendorRouting(analysis.records, columns, vendor), current) : current);
  }
  function updateVendor(nextVendor: string | null) {
    setVendor(nextVendor);
    setRouting(current => analysis ? mergeRouting(deriveVendorRouting(analysis.records, analysis.columns, nextVendor), current) : current);
  }
  function toggleDestination(id: DestinationId) {
    setDestinations(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id]);
  }
  function reset() {
    setPhase('wizard');
    setStep(1);
    setAnalysis(null);
    setDestinations(['teli', 'sendgrid', 'five9']);
    setRouting([]);
    setVendor(null);
    setUploadCount(0);
    setResults({
      teli: 'waiting',
      sendgrid: 'waiting',
      five9: 'waiting'
    });
  }
  return <div className="space-y-6" data-id="element-18" data-mp="1kpzfxp40hgx6">
    {phase === 'wizard' && <div className="rounded-2xl border border-gray-200 bg-white px-5 py-5 shadow-card sm:px-8" data-id="element-19" data-mp="1krk8582usnrv"><Stepper steps={STEPS} current={step} onStepClick={id => setStep(id)} data-id="element-20" data-mp="1fxfzmkdxw7oo" /></div>}
    <AnimatePresence mode="wait" data-id="element-21" data-mp="2ymsj4uxj8g0i"><motion.div key={`${phase}-${step}`} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.25
      }} data-id="element-22" data-mp="2ynk3taplctc0">
      {phase === 'wizard' && step === 1 && <Step1Upload file={analysis?.file ?? null} summary={validation} onFileSelected={setUpload} onRemove={() => setAnalysis(null)} onContinue={() => setStep(2)} data-id="element-23" data-mp="37oibq435hkzv" />}
      {phase === 'wizard' && step === 2 && analysis && <Step2Mapping columns={analysis.columns} records={analysis.records} vendor={vendor} onVendorChange={updateVendor} onChange={updateColumns} onBack={() => setStep(1)} onContinue={() => {
          setRouting(current => mergeRouting(deriveVendorRouting(analysis.records, analysis.columns, vendor), current));
          setStep(3);
        }} data-id="element-24" data-mp="3mjg3auqh66fw" />}
      {phase === 'wizard' && step === 3 && analysis && <Step3Destinations selected={destinations} onToggle={toggleDestination} routing={routing} onRoutingChange={setRouting} onBack={() => setStep(2)} onContinue={() => setStep(4)} data-id="element-25" data-mp="43cb4zprbvls" />}
      {phase === 'wizard' && step === 4 && analysis && validation && <Step4Review selected={destinations} routing={routing} columns={analysis.columns} problems={validation.problems} summary={validation} totalRows={totalRows} onBack={() => setStep(3)} onBackToMapping={() => setStep(2)} onCancel={reset} onSubmit={count => {
          setUploadCount(count);
          setPhase('processing');
        }} data-id="element-26" data-mp="3moni93dyjshi" />}
      {phase === 'processing' && <ProcessingView selected={destinations} totalRecords={uploadCount} onComplete={nextResults => {
          setResults(nextResults);
          setPhase('results');
        }} data-id="element-27" data-mp="38ol7e142hzbr" />}
      {phase === 'results' && <ResultsView selected={destinations} results={results} totalRecords={uploadCount} onStartAnother={reset} onRetry={id => setResults(current => ({
          ...current,
          [id]: 'completed'
        }))} data-id="element-28" data-mp="3inaeqzyc81dr" />}
    </motion.div></AnimatePresence>
  </div>;
}
function mergeRouting(generated: VendorRouting[], current: VendorRouting[]): VendorRouting[] {
  return generated.map(route => {
    const prior = current.find(item => item.vendor === route.vendor);
    return prior && !prior.autoSelected ? {
      ...route,
      teliCampaignId: prior.teliCampaignId,
      sendgridCampaignId: prior.sendgridCampaignId,
      five9ListId: prior.five9ListId,
      autoSelected: false
    } : route;
  });
}